import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  AppError,
  Button,
  Checkbox,
  EmptyState,
  Loader,
  Modal,
  Search,
  toast,
} from "@bfc-software/pallet";
import type { OrgCfgSchemaTypes } from "@bfc-software/api-schemas";
import { logger } from "@bfc-software/app-toolbox";
import { useOrgCfgFetchClient } from "@bfc-software/bfc-web-ui/lib/orgcfg-api-client";

type Candidate =
  OrgCfgSchemaTypes.components["schemas"]["deliverImportCandidate"];
type ImportResult =
  OrgCfgSchemaTypes.components["schemas"]["deliverImportDriversResult"];

/** The service's `{ message }` body on a 4xx/5xx, or the fallback. */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Import drivers from Dakota as Deliver app users.
 *
 * The candidates are the drivers Deliver reports as active (a route started in
 * the last six months), via the config service. A driver who already has an
 * account is listed but cannot be picked. The service takes each new account's
 * name and initial PIN (the Dakota employee number) from Deliver's own list,
 * not from this request, so only the chosen user ids are sent.
 */
export function DriverImportModal({
  isOpen,
  onClose,
  onImported,
  orgId,
  warehouseId,
  t,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Called once an import has gone through, so the driver list can reload. */
  onImported: () => void;
  orgId: string;
  warehouseId: string;
  t: TFunction;
}) {
  const orgCfgClient = useOrgCfgFetchClient();
  const candidatesQuery = useQuery({
    queryKey: ["driver-import-candidates", orgId, warehouseId],
    queryFn: async () => {
      const { data, error } = await orgCfgClient.GET(
        "/orgs/deliver/drivers/import_candidates",
        { params: { query: { orgId, warehouseId } } },
      );
      if (error || !data) throw error ?? new Error("no candidates");
      return data.drivers;
    },
    enabled: isOpen,
    // Always fresh: the list marks who already exists, which an import changes.
    gcTime: 0,
  });

  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [isPending, setIsPending] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const candidates = useMemo(
    () =>
      [...(candidatesQuery.data ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [candidatesQuery.data],
  );
  const importable = candidates.filter((c) => !c.exists);
  const needle = q.trim().toLowerCase();
  const visible = candidates.filter(
    (c) =>
      !needle ||
      c.userId.toLowerCase().includes(needle) ||
      c.name.toLowerCase().includes(needle),
  );
  const visibleImportable = visible.filter((c) => !c.exists);
  const allVisibleSelected =
    visibleImportable.length > 0 &&
    visibleImportable.every((c) => selected.has(c.userId));

  const reset = () => {
    setQ("");
    setSelected(new Set());
    setResult(null);
  };

  // Every dismissal routes through here so Escape, the backdrop and the buttons
  // all clear the picks; otherwise a stale selection or result reappears.
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      reset();
      onClose();
    }
  };

  const toggle = (userId: string, on: boolean) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (on) next.add(userId);
      else next.delete(userId);
      return next;
    });

  const toggleAllVisible = (on: boolean) =>
    setSelected((previous) => {
      const next = new Set(previous);
      for (const c of visibleImportable) {
        if (on) next.add(c.userId);
        else next.delete(c.userId);
      }
      return next;
    });

  const submit = async () => {
    if (isPending || selected.size === 0) return;
    setIsPending(true);
    try {
      const { data, error } = await orgCfgClient.POST(
        "/orgs/deliver/drivers/import",
        { body: { orgId, warehouseId, userIds: [...selected] } },
      );
      if (error || !data) throw error ?? new Error("import failed");
      logger.info("Imported drivers", { ...data });
      setResult(data);
      onImported();
    } catch (error) {
      logger.warn("Failed to import drivers", { error });
      toast.error(
        errorMessage(error, t("manage.drivers.import.failedFallback")),
      );
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Modal
      control="external"
      data-test-role="driver-import-modal"
      isDismissable={!isPending}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      width="wide"
      header={
        <h2 className="mb-4 text-size-18 text-on-surface">
          {result
            ? t("manage.drivers.import.resultTitle")
            : t("manage.drivers.import.title")}
        </h2>
      }
    >
      {/* `Modal` never caps a tall body, so the cap is an inline `style`. */}
      <div
        className="flex max-w-full flex-col overflow-y-auto"
        // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
        style={{ maxHeight: "calc(100vh - 320px)" }}
      >
        {result ? (
          <div className="flex flex-col" data-test-role="driver-import-result">
            <p className="mb-16 text-size-13 text-on-surface">
              {t("manage.drivers.import.resultSummary", {
                imported: result.imported,
                skipped: result.skipped,
                failed: result.failed,
              })}
            </p>
            {result.imported > 0 ? (
              <p className="mb-16 text-size-13 text-on-surface-variant">
                {t("manage.drivers.import.pinNote")}
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-8">
              <Button
                kind="text-only"
                variant="avatar-surface"
                text={t("manage.drivers.done")}
                onPress={() => handleOpenChange(false)}
              />
            </div>
          </div>
        ) : (
          <>
            <p className="mb-16 text-size-13 text-on-surface-variant">
              {t("manage.drivers.import.intro")}
            </p>

            {candidatesQuery.isLoading ? (
              <Loader type="page" />
            ) : candidatesQuery.isError ? (
              <AppError
                description={errorMessage(
                  candidatesQuery.error,
                  t("manage.drivers.import.failed"),
                )}
                resolution={t("manage.failureSupport")}
              />
            ) : candidates.length === 0 ? (
              <EmptyState heading={t("manage.drivers.import.empty")} />
            ) : (
              <>
                <div className="mb-12 flex items-center justify-between gap-12">
                  <Search
                    aria-label={t("manage.drivers.import.searchAriaLabel")}
                    placeholder={t("manage.drivers.searchPlaceholder")}
                    value={q}
                    onChange={(value) => setQ(String(value))}
                    onClear={() => setQ("")}
                  />
                  {/* `flex-none` so a long search box can't squeeze the label onto two lines. */}
                  <div className="flex-none">
                    <Checkbox
                      name="select-all"
                      value="all"
                      label={t("manage.drivers.import.selectAll")}
                      isSelected={allVisibleSelected}
                      isDisabled={visibleImportable.length === 0}
                      onChange={toggleAllVisible}
                    />
                  </div>
                </div>

                <div className="flex flex-col" data-test-role="import-list">
                  {visible.length === 0 ? (
                    <EmptyState heading={t("manage.drivers.empty")} />
                  ) : (
                    visible.map((c: Candidate) => (
                      <div
                        key={c.userId}
                        className="flex items-center justify-between gap-12 border-b border-outline-variant py-8"
                        data-test-role="import-row"
                      >
                        <Checkbox
                          name={`import-${c.userId}`}
                          value={c.userId}
                          label={`${c.name} (${c.userId})`}
                          isSelected={c.exists || selected.has(c.userId)}
                          isDisabled={c.exists}
                          onChange={(on) => toggle(c.userId, on)}
                        />
                        {c.exists ? (
                          <span className="text-size-13 text-on-surface-variant">
                            {t("manage.drivers.import.exists")}
                          </span>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>

                <div className="mt-16 flex items-center justify-between gap-8">
                  <span className="text-size-13 text-on-surface-variant">
                    {t("manage.drivers.import.selectedCount", {
                      selected: selected.size,
                      total: importable.length,
                    })}
                  </span>
                  <div className="flex gap-8">
                    <Button
                      kind="text-only"
                      variant="avatar-surface"
                      text={
                        isPending
                          ? t("manage.drivers.import.importing")
                          : t("manage.drivers.import.submit", {
                              count: selected.size,
                            })
                      }
                      isDisabled={isPending || selected.size === 0}
                      onPress={submit}
                      data-test-role="import-submit"
                    />
                    <Button
                      kind="text-only"
                      variant="tertiary"
                      text={t("manage.drivers.cancel")}
                      onPress={() => handleOpenChange(false)}
                      isDisabled={isPending}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
